import { gql, useQuery } from "@apollo/client";
import { GQL } from "@binale-tech/shared";
import React, { FC, memo, useState } from "react";
import { useGqlMutator } from "../../../scripts/graphql/useGqlMutator";
import { Drawer, Flex, Space } from "antd";
import { ArrowRightOutlined } from "@ant-design/icons";
import { FormattedMessage } from "react-intl";
import { Accordion, AccordionItem, Button, Checkbox, Divider } from "@heroui/react";
import { useSaldoTransferInput } from "./hooks/useSaldoTransferInput";

const fragmentSaldo = gql`
    fragment CompanyTransferSaldoFragment on CompanyTransferSaldoResult {
        id
        yearTo
        userId
        createdAt
        er {
            id
            productId
            groupId
            saldo
        }
        erAnz {
            id
            productId
            groupId
            saldo
        }
        deb {
            id
            productId
            groupId
            saldo
        }
        bank {
            id
            productId
            groupId
            saldo
        }
        kb {
            id
            productId
            groupId
            saldo
        }
    }
`;

export const companyTransferSaldo = gql`
    ${fragmentSaldo}
    mutation companyTransferSaldo($input: CompanyTransferSaldoInput!) {
        companyTransferSaldo(input: $input) {
            ...CompanyTransferSaldoFragment
        }
    }
`;

const companyTransferSaldoLogs = gql`
    ${fragmentSaldo}
    query companyTransferSaldoLogs($companyId: ID!, $yearTo: Int!) {
        companyTransferSaldoLogs(companyId: $companyId, yearTo: $yearTo) {
            ...CompanyTransferSaldoFragment
        }
    }
`;

const CompanySaldoDrawer: FC<{ year: number; onClose: () => void; companyData: GQL.ICompany }> = ({
    year: yearTo,
    onClose,
    companyData,
}) => {
    const Mutator = useGqlMutator();
    const companyId = companyData?.id;
    const open = Boolean(yearTo);

    const [loading, setLoading] = useState(false);
    const [modules, setModules] = useState<Set<GQL.IProductKey>>(new Set());
    const input = useSaldoTransferInput(yearTo, modules, companyData);

    const { loading: logsLoading, data: logsData } = useQuery<
        Pick<GQL.IQuery, "companyTransferSaldoLogs">,
        GQL.IQueryCompanyTransferSaldoLogsArgs
    >(companyTransferSaldoLogs, {
        variables: { companyId, yearTo },
        fetchPolicy: "network-only",
        skip: !companyId || !yearTo,
    });

    if (!companyId) {
        return null;
    }

    const onValueChange = (pk: GQL.IProductKey) => (isSelected: boolean) => {
        setModules(prevState => {
            if (isSelected) {
                prevState.add(pk);
            } else {
                prevState.delete(pk);
            }
            return new Set(prevState);
        });
    };
    const handleTransferSaldo = () => {
        setLoading(true);

        Mutator.mutate<"companyTransferSaldo", GQL.ICompanyTransferSaldoInput>({
            mutation: companyTransferSaldo,
            input,
            throwOnError: true,
        }).finally(() => setLoading(false));
    };

    return (
        <Drawer
            maskClosable
            destroyOnHidden
            width={600}
            onClose={onClose}
            open={open}
            title={
                <Flex style={{ width: "100%" }} justify={"space-between"} align={"center"}>
                    <span>
                        Saldenvortrag: {yearTo - 1}&nbsp;
                        <ArrowRightOutlined />
                        &nbsp;{yearTo}
                    </span>
                </Flex>
            }
        >
            <Space direction={"vertical"} style={{ width: "100%" }}>
                <Checkbox
                    size="lg"
                    isDisabled={loading}
                    isSelected={modules.has(GQL.IProductKey.Er)}
                    onValueChange={onValueChange(GQL.IProductKey.Er)}
                >
                    <FormattedMessage id="app.titles.ER" />
                </Checkbox>
                <Checkbox
                    size="lg"
                    isDisabled={loading}
                    isSelected={modules.has(GQL.IProductKey.ErA)}
                    onValueChange={onValueChange(GQL.IProductKey.ErA)}
                >
                    <FormattedMessage id="app.titles.ER_A" />
                </Checkbox>
                <Checkbox
                    size="lg"
                    isDisabled={loading}
                    isSelected={modules.has(GQL.IProductKey.Deb)}
                    onValueChange={onValueChange(GQL.IProductKey.Deb)}
                >
                    <FormattedMessage id="app.titles.Deb" />
                </Checkbox>
                <Checkbox
                    size="lg"
                    isDisabled={loading}
                    isSelected={modules.has(GQL.IProductKey.Bank)}
                    onValueChange={onValueChange(GQL.IProductKey.Bank)}
                >
                    <FormattedMessage id="app.titles.Bank.pl" />
                </Checkbox>
                <Checkbox
                    size="lg"
                    isDisabled={loading}
                    isSelected={modules.has(GQL.IProductKey.Kb)}
                    onValueChange={onValueChange(GQL.IProductKey.Kb)}
                >
                    <FormattedMessage id="app.titles.KB.pl" />
                </Checkbox>
                <Button
                    size={"lg"}
                    type={"submit"}
                    color="primary"
                    isDisabled={modules.size === 0}
                    isLoading={loading}
                    onPress={handleTransferSaldo}
                >
                    Los
                </Button>

                <Divider style={{ marginTop: 50 }} />

                <Space style={{ height: 32 }}>
                    <strong>Logs {yearTo}</strong>
                    {logsLoading && <Button isLoading={logsLoading} radius={"full"} color={"secondary"} isIconOnly />}
                </Space>
                <Accordion variant={"bordered"}>
                    {logsData?.companyTransferSaldoLogs?.map(log => (
                        <AccordionItem key={log.id} subtitle={log.userId} title={log.createdAt}>
                            <pre>{JSON.stringify(log, null, 2)}</pre>
                        </AccordionItem>
                    ))}
                </Accordion>
            </Space>
        </Drawer>
    );
};

export default memo(CompanySaldoDrawer);
